/**
 * Phase 20G.1a.4f — Authenticated self-healing for signup intents.
 *
 * Mounted once at the app root. When a Katalog session is authenticated,
 * fires ONE idempotent call to POST /api/account-signup/finalize so that
 * any signup intent missed by the callback (interrupted, tab closed,
 * verified on another device, payment-before-verification) is finalized
 * and an eligible paid guest-sentinel order is attached via the canonical
 * convergence RPC.
 *
 * Guards:
 *   - Runs ONLY when authReady && authUser.
 *   - Per-user one-shot via sessionStorage to prevent uncontrolled
 *     reconciliation on every render / navigation.
 *   - Failures are silent (already-authenticated user — no UX surface).
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '@bright-web-skeleton/auth';
import { supabase } from '@bright-web-skeleton/supabase';
import { getBackendUrl } from '../lib/env';
import { finalizeSignupIntentWithRetry } from '../lib/signupIntentFinalize';

const STORAGE_KEY_PREFIX = 'katalog:signupIntentHealed:';

// Only truly terminal outcomes suppress future runs. A non-terminal
// outcome (no_intent, waiting_for_paid_order, transient_exhausted,
// http_error, verified/already_verified without an attach) must remain
// retriable so a webhook that arrives AFTER this run can still cause
// the customer's guest order to be attached on a later visit or
// authenticated-session initialization within the same browser tab.
const TERMINAL_OUTCOMES = new Set([
  'attached',
  'already_attached',
  'unauthorized',
  'conflict',
]);

export function SignupIntentHealer(): null {
  const { authReady, authUser } = useAuth();
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!authReady || !authUser || inFlightRef.current) return;
    const sessionFlag = `${STORAGE_KEY_PREFIX}${authUser.id}`;
    try {
      if (sessionStorage.getItem(sessionFlag)) return;
    } catch { /* storage unavailable — still attempt once */ }
    inFlightRef.current = true;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const outcome = await finalizeSignupIntentWithRetry({
          backendUrl: getBackendUrl(),
          accessToken: token,
          authUserId: authUser.id,
        });
        if (TERMINAL_OUTCOMES.has(outcome.kind)) {
          try { sessionStorage.setItem(sessionFlag, '1'); } catch { /* ignore */ }
        }
        console.warn('[SignupIntentHealer] signup_intent_heal_outcome', {
          user_id: authUser.id,
          outcome: outcome.kind,
          attempts: outcome.attempts,
          intent_id: outcome.intentId ?? null,
        });
      } catch {
        console.warn('[SignupIntentHealer] heal_unexpected_error', { user_id: authUser.id });
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [authReady, authUser]);

  return null;
}